import { useMutation } from '@tanstack/react-query';
import { toast } from '@workspace/ui/components/sonner';
import { useState } from 'react';

import { useAuth } from '~/auth/use-auth';
import { useCreateInvitation } from '../../persons/-hooks/use-create-invitation';
import { createOrganizationApi } from '../-api/api';
import type { OrganizationForm } from '../-schemas/org-schema';

export function useOrganizationForm() {
  const { updateMetadata } = useAuth();
  const mutation = useMutation({
    mutationFn: createOrganizationApi,
  });
  const createInvitationMutation = useCreateInvitation();

  const [businessType, setBusinessType] = useState<
    'from-scratch' | 'existing' | null
  >(null);

  const [formData, setFormData] = useState<OrganizationForm>({
    whereAreYouStart: {
      name: '',
    },
    typeOfOrganization: {
      type: '',
    },
    businessAddress: {
      legalName: '',
      taxId: '',
      whatYourCompanyDoes: '',
      country: '',
      state: '',
      address1: '',
      postalCode: '',
    },
    teamMembers: [],
  });

  const updateStepData = <T extends keyof OrganizationForm>(
    step: T,
    data: Partial<OrganizationForm[T]>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [step]: Array.isArray(data)
        ? data
        : {
            ...prev[step],
            ...data,
          },
    }));
  };

  const createOrganization = async () => {
    const body = formData;
    const res = await mutation.mutateAsync({
      org_type: 'business',
      profile: {
        legal_name: body.businessAddress.legalName,
        tax_id: body.businessAddress.taxId ?? '',
        business_type: body.typeOfOrganization.type,
        incorporation_country_code: body.businessAddress.country,
        incorporation_state: body.businessAddress.state,
        address_line1: body.businessAddress.address1,
        postal_code: body.businessAddress.postalCode,
        places: [],
      },
      metadata: {
        industry: 'Technology',
      },
    });
    if (!res.data) {
      toast.error('Error al crear la organización');
      return;
    }
    toast.success('Organización creada exitosamente');
    console.log(body.teamMembers);
    await Promise.all(
      body.teamMembers.map(async (member) => {
        await createInvitationMutation.mutateAsync({
          // @ts-expect-error
          orgUrn: res.data?.result?.organization?.urn,
          payload: {
            type: 'member',
            details: {
              membership_info: {
                title: 'Admin',
                name: member.name,
                email: member.email,
              },
              roles: [member.role],
              scopes: ['org.read', 'team.read', 'member.read.self'],
              message: `¡Bienvenido a ${body.businessAddress.legalName}!`,
            },
            channel: 'email',
            channel_routing: {
              email: member.email,
            },
            metadata: {
              source: 'org_creation',
              priority: 'high',
            },
          },
        });
      }),
    );
    toast.success(`${body.teamMembers.length} invitaciones enviadas`);

    await updateMetadata({
      organization: true,
      onboarding: true,
    });
  };

  return {
    formData,
    updateStepData,
    createOrganization,
    businessType,
    setBusinessType,
  };
}
